/**
 * Trilho horizontal com setas (<e-rail>).
 *
 * O trilho é um [data-rail-track] com scroll-snap: no celular rola no dedo; a
 * partir do tablet, as setas [data-rail-prev] e [data-rail-next] avançam um
 * card por vez. Sem autoplay. As setas desativam nas pontas e somem quando
 * tudo cabe na tela (data-static). Desativar é aria-disabled, não disabled:
 * um botão disabled perde o foco, e quem usa teclado voltaria ao topo.
 */

class ERail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.previousButton = this.querySelector('[data-rail-prev]');
    this.nextButton = this.querySelector('[data-rail-next]');
    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.onClick = (event) => {
      const button = event.target.closest('[data-rail-prev], [data-rail-next]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      this.step(button === this.previousButton ? -1 : 1);
    };
    this.onScroll = () => this.scheduleUpdate();
    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  step(direction) {
    const firstItem = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = firstItem ? firstItem.getBoundingClientRect().width + gap : this.track.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  scheduleUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    this.toggleAttribute('data-static', maxScroll <= 1);
    this.previousButton.setAttribute('aria-disabled', String(this.track.scrollLeft <= 1));
    this.nextButton.setAttribute('aria-disabled', String(this.track.scrollLeft >= maxScroll - 1));
  }
}

if (!customElements.get('e-rail')) customElements.define('e-rail', ERail);
